/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Pressable, Icon } from '@components/atoms'
import { FullModalLayout, Modal } from '@components/molecules'
import {
	BoDetailSections,
	BoDetailSection,
} from '@components/systems/backoffice/BoDetailSections'
import {
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	SelectChangeEvent,
	TextField as MuiTextField,
} from '@mui/material'
import {
	useAppSelector,
	useAppDispatch,
	shipmentActions,
	productActions,
	routeActions,
	ApiGetDataStatus,
} from '@redux/index'
import {
	DispatchDetailType,
	DispatchType,
	DispatchItemType,
	DispatchItemInputType,
	DispatchUpdateType,
	ShipmentType,
	ProductType,
} from '@schemas'
import {
	mono,
	boFont,
	boShared,
	boStatusChipClass,
	boMedia,
	boTableAsCards,
} from '@components/systems/backoffice/backoffice.tokens'
import {
	DISPATCH_KIND_TITLE,
	DISPATCH_STATUS_TITLE,
	DISPATCH_CARRIER_OPTIONS,
	DISPATCH_CARRIER_TITLE,
	MANUAL_DISPATCH_KINDS,
	dispatchTrackUrl,
} from '@src/configs/dispatch.config'
import { AsYouType, CountryCode, isValidPhoneNumber } from 'libphonenumber-js'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

const EMPTY = '—'

/** FedEx 라벨 자체의 상태 — 취소된 건까지 이력에 선다 */
const SHIPMENT_STATUS_TITLE: { [key: string]: string } = {
	created: '라벨 생성',
	inTransit: '배송중',
	delivered: '배송 완료',
	canceled: '취소',
}
const SHIPMENT_STATUS_KEY: { [key: string]: string } = {
	created: 'preparing',
	inTransit: 'inTransit',
	delivered: 'completed',
	canceled: 'deleted',
}

export const LIST_PATH = '/main/shipping'
export const detailPath = (dispatchId: string): string =>
	`${LIST_PATH}/${encodeURIComponent(dispatchId)}`
export const labelNewPath = (dispatchId: string): string =>
	`${detailPath(dispatchId)}/label/new`
export const labelViewPath = (dispatchId: string, shipmentId?: string): string =>
	shipmentId === undefined || shipmentId === ''
		? `${detailPath(dispatchId)}/label`
		: `${detailPath(dispatchId)}/label/${encodeURIComponent(shipmentId)}`

const datetime = (value: string): string =>
	value !== '' ? dayjs(value).tz().format('YYYY-MM-DD HH:mm') : EMPTY
/** 날짜 입력 칸 값 — 'YYYY-MM-DD' */
const dateInput = (value: string): string =>
	value !== '' ? dayjs(value).tz().format('YYYY-MM-DD') : ''

const clean = (v: string | null | undefined): string =>
	v === null || v === undefined || v === 'undefined' || v === 'null' ? '' : String(v)

/**
 * 국제 형식으로. 주문 건의 전화는 국가번호가 앞에 붙은 숫자열(`14236024060`)이라 `+` 를 붙여야
 * 파싱된다. 판정이 안 되면 원문 그대로 둔다 — 라벨에 나가는 값이 정본이다.
 */
const formatPhone = (phone: string, countryCode: string): string => {
	const raw = clean(phone)
	if (raw === '') return EMPTY
	const withPlus = raw.startsWith('+') ? raw : `+${raw}`
	try {
		if (isValidPhoneNumber(withPlus)) {
			return new AsYouType().input(withPlus)
		}
		if (isValidPhoneNumber(raw, countryCode as CountryCode)) {
			return new AsYouType(countryCode as CountryCode).input(raw)
		}
	} catch {
		/* 형식 판정 실패 — 원문으로 */
	}
	return raw
}

/** 화면이 편집하는 품목 줄 */
interface ItemDraft extends DispatchItemInputType {
	name: string
	productCode: string
}

const toDraft = (i: DispatchItemType): ItemDraft => ({
	id: i.id,
	productId: i.productId,
	freeName: i.freeName,
	serial: i.serial,
	boxNo: i.boxNo,
	name: i.name,
	productCode: i.productCode,
})

/**
 * 배송 건 자세히 — 배송 관리 그리드의 "자세히" 열이 연다. 전체 화면 오버레이 (DISPATCH_PLAN §3.7).
 *
 * 한 건 = 박스 하나. 수취인 · 품목 줄(S/N · 박스 이동) · 배송(배송사 · 송장 · 발송 · 완료 · 비고) ·
 * FedEx 라벨 이력 · 추적. **결제는 여기 없다** — 금액·환불은 주문 자세히가 정본이고 하단 링크로 건너간다.
 *
 * 주문 건은 종류·거래처·수취인이 주문에 묶여 있어 읽기만 하고, 수동 건은 여기서 고친다.
 * 활성 FedEx 라벨이 있으면 배송사·송장은 라벨이 정본이라 잠긴다.
 */
export const DispatchDetail = () => {
	const reduxDispatch = useAppDispatch()

	const dispatchId: string = useAppSelector(state => state.shipment.detailDispatchId)
	const detail: DispatchDetailType | null = useAppSelector(state => state.shipment.detail)
	const detailStatus: ApiGetDataStatus = useAppSelector(
		state => state.shipment.detailStatus,
	)
	const list: DispatchType[] = useAppSelector(state => state.shipment.shipmentList)
	const tracking: boolean = useAppSelector(state => state.shipment.tracking)
	const saving: boolean = useAppSelector(state => state.shipment.saving)
	const products: ProductType[] = useAppSelector(state => state.product.products)

	// 품목 줄 추가에 쓰는 제품 목록 — 한 번만 읽는다
	React.useEffect(() => {
		if (products.length === 0) {
			reduxDispatch(productActions.TRIGGER_INIT_PRODUCT_PAGE(true))
		}
	}, [products.length, reduxDispatch])

	const safeList: DispatchType[] = Array.isArray(list) ? list : []
	const index = React.useMemo(
		() => safeList.findIndex(s => s?.id === dispatchId),
		[safeList, dispatchId],
	)

	const dispatch = detail?.dispatch
	const prefill = detail?.prefill
	const shipments: ShipmentType[] = Array.isArray(detail?.shipments)
		? (detail?.shipments as ShipmentType[])
		: []
	const boxes = detail?.boxes ?? []

	// ── 폼 ──────────────────────────────────────────────────────────
	const [kind, setKind] = React.useState<string>('repair')
	const [partyName, setPartyName] = React.useState<string>('')
	const [orderedAt, setOrderedAt] = React.useState<string>('')
	const [carrier, setCarrier] = React.useState<string>('')
	const [trackingNumber, setTrackingNumber] = React.useState<string>('')
	const [shippedAt, setShippedAt] = React.useState<string>('')
	const [deliveredAt, setDeliveredAt] = React.useState<string>('')
	const [memo, setMemo] = React.useState<string>('')
	const [receiverName, setReceiverName] = React.useState<string>('')
	const [receiverPhone, setReceiverPhone] = React.useState<string>('')
	const [address1, setAddress1] = React.useState<string>('')
	const [address2, setAddress2] = React.useState<string>('')
	const [city, setCity] = React.useState<string>('')
	const [stateName, setStateName] = React.useState<string>('')
	const [postalCode, setPostalCode] = React.useState<string>('')
	const [countryIso, setCountryIso] = React.useState<string>('')
	const [items, setItems] = React.useState<ItemDraft[]>([])
	const [newProductId, setNewProductId] = React.useState<string>('')
	const [newFreeName, setNewFreeName] = React.useState<string>('')
	const [confirmDelete, setConfirmDelete] = React.useState<boolean>(false)

	// 서버 값 → 폼. 다른 건으로 옮기거나 저장 뒤 다시 읽으면 폼도 따라간다
	React.useEffect(() => {
		if (dispatch === undefined) return
		setKind(dispatch.kind)
		setPartyName(dispatch.partyName)
		setOrderedAt(dateInput(dispatch.orderedAt))
		setCarrier(dispatch.carrier)
		setTrackingNumber(dispatch.trackingNumber)
		setShippedAt(dateInput(dispatch.shippedAt))
		setDeliveredAt(dateInput(dispatch.deliveredAt))
		setMemo(dispatch.memo)
		setReceiverName(dispatch.receiverName)
		setReceiverPhone(dispatch.receiverPhone)
		setAddress1(dispatch.address1)
		setAddress2(dispatch.address2)
		setCity(dispatch.city)
		setStateName(dispatch.state)
		setPostalCode(dispatch.postalCode)
		setCountryIso(dispatch.countryIso)
		setItems((detail?.items ?? []).map(toDraft))
	}, [dispatch, detail?.items])

	const onClose = React.useCallback(() => {
		reduxDispatch(routeActions.NAVIGATE_PATH(LIST_PATH))
	}, [reduxDispatch])

	const goTo = (nextIndex: number) => {
		const next = safeList[nextIndex]
		if (next !== undefined) {
			reduxDispatch(routeActions.NAVIGATE_PATH(detailPath(next.id)))
		}
	}

	if (dispatchId === '') return null

	const headTitle = (d: DispatchType | undefined): string =>
		d === undefined ? dispatchId : d.orderId !== '' ? d.orderId : d.partyName || d.id

	// 아직 못 찾았다 — 읽는 중이거나, 지워진 건이다
	if (dispatch === undefined || prefill === undefined) {
		return (
			<FullModalLayout
				overlay
				size="large"
				title={dispatchId}
				onCloseClick={onClose}
				customTitle={
					<View className="shipHead" css={headStyle}>
						<span className="boKicker">배송 건</span>
						<View className="shipHeadRow">
							<h1 className="shipNumber">{dispatchId}</h1>
						</View>
					</View>
				}
			>
				<View css={style}>
					<View className="boSection">
						<Text className="boEmpty">
							{detailStatus === 'loading' || detailStatus === 'init'
								? '배송 건을 읽는 중…'
								: '이 배송 건을 찾지 못했습니다. 지워진 건입니다.'}
						</Text>
						{detailStatus === 'empty' && (
							<button type="button" className="boBtn" onClick={onClose}>
								목록으로
							</button>
						)}
					</View>
				</View>
			</FullModalLayout>
		)
	}

	const isManual = dispatch.orderId === ''
	const active = shipments.find(s => s.canceledAt === '')
	const fedexLocked = active !== undefined
	const hasLabel = active !== undefined && active.hasLabel === 1
	const canCreateLabel = dispatch.trackingNumber === '' && active === undefined
	const canDelete = !(dispatch.orderId !== '' && dispatch.boxNo === 1) && !fedexLocked
	const trackUrl = dispatchTrackUrl(dispatch.carrier, dispatch.trackingNumber)

	const addressLine2 = [
		clean(dispatch.city),
		clean(dispatch.state),
		clean(dispatch.postalCode),
		clean(dispatch.countryIso),
	]
		.filter(s => s !== '')
		.join(', ')

	const onSave = () => {
		const body: DispatchUpdateType = {
			carrier,
			trackingNumber: trackingNumber.trim(),
			shippedAt,
			deliveredAt,
			memo,
			items: items.map(i => ({
				id: i.id,
				productId: i.productId,
				freeName: i.freeName,
				serial: i.serial.trim(),
				boxNo: i.boxNo,
			})),
		}
		if (isManual) {
			body.kind = kind
			body.partyName = partyName.trim()
			body.orderedAt = orderedAt
			body.receiverName = receiverName.trim()
			body.receiverPhone = receiverPhone.trim()
			body.address1 = address1.trim()
			body.address2 = address2.trim()
			body.city = city.trim()
			body.state = stateName.trim()
			body.postalCode = postalCode.trim()
			body.countryIso = countryIso.trim().toUpperCase()
		}
		reduxDispatch(
			shipmentActions.TRIGGER_UPDATE_DISPATCH({ dispatchId: dispatch.id, body }),
		)
	}

	const onAddItem = () => {
		if (newProductId === '' && newFreeName.trim() === '') return
		const product = products.find(p => p.id === newProductId)
		setItems(prev => [
			...prev,
			{
				id: '',
				productId: product?.id ?? '',
				freeName: product === undefined ? newFreeName.trim() : '',
				serial: '',
				boxNo: dispatch.boxNo,
				name: product?.name ?? newFreeName.trim(),
				productCode: product?.code ?? '',
			},
		])
		setNewProductId('')
		setNewFreeName('')
	}

	const onTrackClick = () => {
		reduxDispatch(
			shipmentActions.TRIGGER_TRACK({
				shipmentIds: [],
				orderIds: [],
				dispatchIds: [dispatch.id],
				refreshOrders: false,
			}),
		)
	}

	return (
		<FullModalLayout
			overlay
			size="large"
			title={headTitle(dispatch)}
			onCloseClick={onClose}
			leftSideContent={
				index < 0 ? undefined : (
					<View className="shipNav" css={navStyle}>
						<Pressable
							className="shipNavBtn"
							disabled={index <= 0}
							onClick={() => goTo(index - 1)}
						>
							<Icon name="chevron-left" className="shipNavIcon" />
							<span>이전</span>
						</Pressable>
						<span className="shipNavPos">
							{index + 1} / {safeList.length}
						</span>
						<Pressable
							className="shipNavBtn"
							disabled={index >= safeList.length - 1}
							onClick={() => goTo(index + 1)}
						>
							<span>다음</span>
							<Icon name="chevron-right" className="shipNavIcon" />
						</Pressable>
					</View>
				)
			}
			customTitle={
				<View className="shipHead" css={headStyle}>
					<span className="boKicker">배송 건</span>
					<View className="shipHeadRow">
						<h1 className="shipNumber">{headTitle(dispatch)}</h1>
						{dispatch.boxCount > 1 && (
							<span className="shipBox">
								박스 {dispatch.boxNo} / {dispatch.boxCount}
							</span>
						)}
						<span
							className={`boChip${
								dispatch.direction === 'in' ? ' faint' : !isManual ? ' outline' : ''
							}`}
						>
							{DISPATCH_KIND_TITLE[dispatch.kind] ?? dispatch.kind}
						</span>
						{/* 칩 하나 — 환불·취소면 상태 자체가 그 값이다(`Dispatch.ladder`) */}
						<span className={boStatusChipClass(dispatch.status)}>
							{DISPATCH_STATUS_TITLE[dispatch.status] ?? dispatch.status}
						</span>
					</View>
					<span className="shipHeadMeta">
						{[
							`주문일 ${datetime(dispatch.orderedAt)}`,
							dispatch.channel !== ''
								? dispatch.channel === 'wholesale'
									? '도매'
									: '소매'
								: '수동 건',
							clean(dispatch.seasonId) !== '' ? `시즌 ${dispatch.seasonId}` : '',
							clean(dispatch.customerEmail),
						]
							.filter(aPart => aPart !== '')
							.join(' · ')}
					</span>
				</View>
			}
		>
			<View css={style}>
				<BoDetailSections>
					<BoDetailSection id="info" label="정보">
						{/* 큰 수 */}
						<View className="statRow">
							<View className="boStat">
								<span className="boKicker">상태</span>
								<span className="boStatValue statText">
									{DISPATCH_STATUS_TITLE[dispatch.status] ?? dispatch.status}
								</span>
							</View>
							<View className="boStat">
								<span className="boKicker">송장</span>
								<span className="boStatValue statText">
									{dispatch.trackingNumber !== '' ? (
										trackUrl !== '' ? (
											<a href={trackUrl} target="_blank" rel="noreferrer">
												{dispatch.trackingNumber}
											</a>
										) : (
											dispatch.trackingNumber
										)
									) : (
										<span className="statFaint">아직 없음</span>
									)}
								</span>
							</View>
							<View className="boStat">
								<span className="boKicker">배송사 · 박스</span>
								<span className="boStatValue statText">
									{[
										dispatch.carrier !== ''
											? DISPATCH_CARRIER_TITLE[dispatch.carrier] ?? dispatch.carrier
											: EMPTY,
										active !== undefined
											? `${active.boxCode || EMPTY} · ${active.weightKg} kg`
											: dispatch.boxCode !== ''
											? `${dispatch.boxCode} · ${dispatch.weightKg} kg`
											: '',
									]
										.filter(aPart => aPart !== '')
										.join(' · ')}
								</span>
							</View>
							<View className="boStat">
								<span className="boKicker">발송 · 완료</span>
								<span className="boStatValue statText">
									{dispatch.shippedAt !== '' ? dateInput(dispatch.shippedAt) : EMPTY}
									{dispatch.deliveredAt !== '' && ` → ${dateInput(dispatch.deliveredAt)}`}
								</span>
							</View>
						</View>

						{/* 수취인 — 주문 건은 라벨에 찍히는 값 그대로(읽기), 수동 건은 여기서 고친다 */}
						<View className="boSection">
							<Text className="boSectionTitle">
								{isManual ? '거래처 · 수취인' : '수취인'}
							</Text>
							{isManual ? (
								<>
									<View className="formRow three">
										<FormControl size="small">
											<InputLabel id="dispatch-kind-select">종류</InputLabel>
											<Select
												labelId="dispatch-kind-select"
												label="종류"
												value={kind}
												onChange={(e: SelectChangeEvent) => setKind(e.target.value)}
											>
												{MANUAL_DISPATCH_KINDS.map(k => (
													<MenuItem key={k.value} value={k.value}>
														{k.label}
													</MenuItem>
												))}
											</Select>
										</FormControl>
										<MuiTextField
											label="거래처 / 고객"
											size="small"
											value={partyName}
											onChange={e => setPartyName(e.target.value)}
										/>
										<MuiTextField
											label="주문일"
											size="small"
											type="date"
											value={orderedAt}
											onChange={e => setOrderedAt(e.target.value)}
											InputLabelProps={{ shrink: true }}
										/>
									</View>
									<View className="formRow three">
										<MuiTextField
											label="수취인"
											size="small"
											value={receiverName}
											onChange={e => setReceiverName(e.target.value)}
										/>
										<MuiTextField
											label="전화"
											size="small"
											value={receiverPhone}
											onChange={e => setReceiverPhone(e.target.value)}
										/>
										<MuiTextField
											label="국가(ISO 2)"
											size="small"
											value={countryIso}
											onChange={e =>
												setCountryIso(e.target.value.toUpperCase().slice(0, 2))
											}
										/>
									</View>
									<View className="formRow">
										<MuiTextField
											label="주소 1"
											size="small"
											value={address1}
											onChange={e => setAddress1(e.target.value)}
										/>
										<MuiTextField
											label="주소 2"
											size="small"
											value={address2}
											onChange={e => setAddress2(e.target.value)}
										/>
									</View>
									<View className="formRow three">
										<MuiTextField
											label="시"
											size="small"
											value={city}
											onChange={e => setCity(e.target.value)}
										/>
										<MuiTextField
											label="주 / 도"
											size="small"
											value={stateName}
											onChange={e => setStateName(e.target.value)}
										/>
										<MuiTextField
											label="우편번호"
											size="small"
											value={postalCode}
											onChange={e => setPostalCode(e.target.value)}
										/>
									</View>
								</>
							) : (
								<>
									<View className="boSpecRow">
										<span className="boSpecLabel">거래처 / 고객</span>
										<span className="boSpecValue">{dispatch.partyName || EMPTY}</span>
									</View>
									<View className="boSpecRow">
										<span className="boSpecLabel">이름</span>
										<span className="boSpecValue">{dispatch.receiverName || EMPTY}</span>
									</View>
									<View className="boSpecRow">
										<span className="boSpecLabel">전화</span>
										<span className="boSpecValue">
											{formatPhone(dispatch.receiverPhone, dispatch.countryIso)}
										</span>
									</View>
									<View className="boSpecRow top">
										<span className="boSpecLabel">주소</span>
										<span className="boSpecValue">
											<span className="block">
												{[dispatch.address1, dispatch.address2]
													.filter(s => clean(s) !== '')
													.join(', ') || EMPTY}
											</span>
											<span className="block">{addressLine2 || EMPTY}</span>
										</span>
									</View>
									<View className="boSpecRow">
										<span className="boSpecLabel">이메일</span>
										<span className="boSpecValue">{dispatch.customerEmail || EMPTY}</span>
									</View>
								</>
							)}
						</View>
					</BoDetailSection>
					<BoDetailSection id="items" label="품목 · S/N">
						{/* 품목 줄 — 한 행 = 한 대. S/N 을 채우고, 박스가 여럿이면 줄을 옮긴다 */}
						<View className="boSection">
							<Text className="boSectionTitle">품목 · S/N</Text>
							{items.length === 0 ? (
								<Text className="boEmpty">품목 줄이 없습니다. 아래에서 추가합니다.</Text>
							) : (
								<table className="lineTable">
									<thead>
										<tr>
											<th className="idx">#</th>
											<th>품목</th>
											<th>S/N</th>
											{boxes.length > 1 && <th>박스</th>}
											<th className="num"> </th>
										</tr>
									</thead>
									<tbody>
										{items.map((it, idx) => (
											<tr key={it.id !== '' ? it.id : `new-${idx}`}>
												<td className="idx">{idx + 1}</td>
												<td className="cardTitle">
													{it.productCode !== '' && (
														<span className="itemCode">{it.productCode}</span>
													)}
													{it.name || EMPTY}
												</td>
												<td data-label="S/N">
													<input
														className="boInput serialInput"
														value={it.serial}
														placeholder="시리얼"
														onChange={e =>
															setItems(prev =>
																prev.map((p, i) =>
																	i === idx ? { ...p, serial: e.target.value } : p,
																),
															)
														}
													/>
												</td>
												{boxes.length > 1 && (
													<td data-label="박스">
														<select
															className="boxSelect"
															value={it.boxNo ?? dispatch.boxNo}
															onChange={e =>
																setItems(prev =>
																	prev.map((p, i) =>
																		i === idx
																			? { ...p, boxNo: Number(e.target.value) }
																			: p,
																	),
																)
															}
														>
															{boxes.map(b => (
																<option key={b.id} value={b.boxNo}>
																	박스 {b.boxNo}
																</option>
															))}
														</select>
													</td>
												)}
												<td className="num cardEnd">
													<button
														type="button"
														className="boBtnText"
														onClick={() =>
															setItems(prev => prev.filter((_, i) => i !== idx))
														}
													>
														빼기
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							)}
							<View className="addRow">
								<FormControl size="small" className="addProduct">
									<InputLabel id="dispatch-add-product">제품</InputLabel>
									<Select
										labelId="dispatch-add-product"
										label="제품"
										value={newProductId}
										onChange={(e: SelectChangeEvent) => setNewProductId(e.target.value)}
									>
										<MenuItem value="">(부품 · 자유 텍스트)</MenuItem>
										{products.map(p => (
											<MenuItem key={p.id} value={p.id}>
												{p.code} · {p.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<MuiTextField
									label="부품명"
									size="small"
									value={newFreeName}
									disabled={newProductId !== ''}
									onChange={e => setNewFreeName(e.target.value)}
									placeholder="PCB · TRS 케이블 …"
								/>
								<button type="button" className="boBtnGhost" onClick={onAddItem}>
									줄 추가
								</button>
							</View>
							<Text className="boNote">
								{boxes.length > 1
									? '박스 열에서 다른 박스를 고르면 저장할 때 그 박스로 옮겨진다.'
									: '한 행이 한 대다. 박스를 나누려면 아래 「박스 추가」.'}
							</Text>
						</View>
					</BoDetailSection>
					<BoDetailSection id="info">
						{/* 배송 — 배송사 · 송장 · 발송 · 완료 · 비고. FedEx 라벨이 있으면 배송사·송장은 라벨이 정본 */}
						<View className="boSection">
							<Text className="boSectionTitle">배송</Text>
							<View className="formRow three">
								<FormControl size="small" disabled={fedexLocked}>
									<InputLabel id="dispatch-carrier-select" shrink>
										배송사
									</InputLabel>
									<Select
										labelId="dispatch-carrier-select"
										label="배송사"
										value={carrier}
										displayEmpty
										onChange={(e: SelectChangeEvent) => setCarrier(e.target.value)}
									>
										<MenuItem value="">(없음)</MenuItem>
										{DISPATCH_CARRIER_OPTIONS.map(c => (
											<MenuItem key={c.value} value={c.value}>
												{c.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<MuiTextField
									label="송장"
									size="small"
									value={trackingNumber}
									disabled={fedexLocked}
									onChange={e => setTrackingNumber(e.target.value)}
									helperText={
										fedexLocked ? 'FedEx 라벨이 정본 — 라벨을 취소해야 바뀐다' : undefined
									}
								/>
								<MuiTextField
									label="비고"
									size="small"
									value={memo}
									onChange={e => setMemo(e.target.value)}
								/>
							</View>
							<View className="formRow three">
								<MuiTextField
									label="발송일"
									size="small"
									type="date"
									value={shippedAt}
									onChange={e => setShippedAt(e.target.value)}
									InputLabelProps={{ shrink: true }}
									helperText="비워 두고 송장을 적으면 오늘로 채워진다"
								/>
								<MuiTextField
									label="배송 완료"
									size="small"
									type="date"
									value={deliveredAt}
									onChange={e => setDeliveredAt(e.target.value)}
									InputLabelProps={{ shrink: true }}
									helperText="FedEx 는 추적 갱신이 채운다. 국내 택배는 손으로"
								/>
								<View />
							</View>
						</View>
					</BoDetailSection>
					<BoDetailSection id="labels" label="라벨">
						{/* FedEx 라벨 이력 */}
						<View className="boSection">
							<Text className="boSectionTitle">FedEx 라벨</Text>
							{shipments.length === 0 ? (
								<Text className="boEmpty">
									라벨이 없습니다. 국내 택배는 위 배송 칸에 송장을 적는다.
								</Text>
							) : (
								<table className="lineTable">
									<thead>
										<tr>
											<th>상태</th>
											<th>송장</th>
											<th>서비스</th>
											<th>박스 · 치수 · 무게</th>
											<th>라벨</th>
											<th className="num">생성</th>
										</tr>
									</thead>
									<tbody>
										{shipments.map(s => (
											<tr key={s.id}>
												<td className="cardTitle">
													<span
														className={boStatusChipClass(
															SHIPMENT_STATUS_KEY[s.status] ?? s.status,
														)}
													>
														{SHIPMENT_STATUS_TITLE[s.status] ?? s.status}
													</span>
												</td>
												<td data-label="송장">
													{s.trackingNumber !== '' ? (
														<a
															href={`https://www.fedex.com/fedextrack/?trknbr=${s.trackingNumber}`}
															target="_blank"
															rel="noreferrer"
														>
															{s.trackingNumber}
														</a>
													) : (
														EMPTY
													)}
												</td>
												<td data-label="서비스">{s.serviceType || EMPTY}</td>
												<td data-label="박스">
													{[
														s.boxCode,
														`${s.widthCm}×${s.heightCm}×${s.depthCm} cm`,
														`${s.weightKg} kg`,
													]
														.filter(aPart => clean(aPart) !== '')
														.join(' · ')}
												</td>
												<td data-label="라벨">
													{s.hasLabel === 1 ? (
														<Pressable
															className="linkBtn"
															onClick={() =>
																reduxDispatch(
																	routeActions.NAVIGATE_PATH(
																		labelViewPath(dispatch.id, s.id),
																	),
																)
															}
														>
															보기
														</Pressable>
													) : (
														EMPTY
													)}
												</td>
												<td className="num" data-label="생성">
													{datetime(s.createdAt)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							)}
						</View>
					</BoDetailSection>
					<BoDetailSection id="tracking" label="추적">
						{/* 추적 — FedEx 만 */}
						{active !== undefined && (
							<View className="boSection">
								<Text className="boSectionTitle">추적</Text>
								<View className="boSpecRow">
									<span className="boSpecLabel">최근 상태</span>
									<span className="boSpecValue">
										{dispatch.lastStatusDesc || EMPTY}
										{dispatch.lastTrackedAt !== '' &&
											` · ${datetime(dispatch.lastTrackedAt)}`}
									</span>
								</View>
								{dispatch.scanEvents.length === 0 ? (
									<Text className="boEmpty">스캔 이력이 아직 없습니다.</Text>
								) : (
									<View className="scanList">
										{dispatch.scanEvents.map((e, i) => (
											<View className="scanRow" key={i}>
												<span className="scanDate">
													{e.date !== '' ? dayjs(e.date).format('MM-DD HH:mm') : ''}
												</span>
												<span className="scanDesc">{e.description}</span>
												<span className="scanLoc">{e.location}</span>
											</View>
										))}
									</View>
								)}
							</View>
						)}
					</BoDetailSection>
				</BoDetailSections>
				{/* 동작 바 */}
				<View className="actionBar">
					<View className="actionLeft">
						<button type="button" className="boBtn" disabled={saving} onClick={onSave}>
							{saving ? '저장 중…' : '저장'}
						</button>
						{canCreateLabel ? (
							<button
								type="button"
								className="boBtnGhost"
								onClick={() =>
									reduxDispatch(routeActions.NAVIGATE_PATH(labelNewPath(dispatch.id)))
								}
							>
								FedEx 라벨 생성
							</button>
						) : (
							<button
								type="button"
								className="boBtnGhost"
								disabled={!hasLabel}
								onClick={() =>
									reduxDispatch(
										routeActions.NAVIGATE_PATH(
											labelViewPath(dispatch.id, active?.id ?? ''),
										),
									)
								}
							>
								라벨 보기 · 인쇄
							</button>
						)}
						{active !== undefined && (
							<button
								type="button"
								className="boBtnGhost"
								disabled={tracking}
								onClick={onTrackClick}
							>
								{tracking ? '조회 중…' : '추적 갱신'}
							</button>
						)}
						{!isManual && (
							<button
								type="button"
								className="boBtnGhost"
								disabled={saving}
								onClick={() =>
									reduxDispatch(shipmentActions.TRIGGER_ADD_BOX(dispatch.id))
								}
							>
								박스 추가
							</button>
						)}
						<button
							type="button"
							className="boBtnText"
							disabled={!canDelete || saving}
							title={
								!canDelete
									? fedexLocked
										? '라벨이 붙어 있다 — 라벨을 먼저 취소한다'
										: '주문 건의 첫 박스는 지울 수 없다'
									: undefined
							}
							onClick={() => setConfirmDelete(true)}
						>
							삭제
						</button>
					</View>
					{!isManual && (
						<Pressable
							className="linkBtn"
							onClick={() =>
								reduxDispatch(
									routeActions.NAVIGATE_PATH(
										`/main/order/${encodeURIComponent(dispatch.orderId)}`,
									),
								)
							}
						>
							주문 자세히 (결제 · 환불)
						</Pressable>
					)}
				</View>
			</View>

			<Modal
				show={confirmDelete}
				target="dispatchDelete"
				title="배송 건 삭제"
				submitTitle="삭제"
				closeTitle="취소"
				isTitleDanger
				onSubmit={() => {
					setConfirmDelete(false)
					reduxDispatch(shipmentActions.TRIGGER_DELETE_DISPATCH(dispatch.id))
				}}
				onCloseModal={() => setConfirmDelete(false)}
			>
				<Text>
					{dispatch.orderId !== ''
						? `박스 ${dispatch.boxNo} 을 지운다. 남은 품목 줄은 박스 1 로 돌아간다.`
						: '이 배송 건을 지운다. 목록에서 사라진다(소프트 삭제).'}
				</Text>
			</Modal>
		</FullModalLayout>
	)
}

const navStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: 4,
	fontFamily: boFont.ui,

	'.shipNavBtn': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 2,
		height: 32,
		padding: '0 8px',
		borderRadius: 2,
		fontSize: 13,
		fontWeight: 500,
		color: mono.dim,
		transition: 'color 200ms ease-out',
		'&:hover:not(:disabled)': { color: mono.ink },
		'&:disabled': { opacity: 0.3, cursor: 'default' },
		'.shipNavIcon': { fontSize: 18, color: 'inherit' },
	},
	'.shipNavPos': {
		padding: '0 6px',
		fontSize: 12,
		color: mono.faint,
		fontVariantNumeric: 'tabular-nums',
	},
})

const headStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: 8,
	fontFamily: boFont.ui,

	'.boKicker': {
		fontSize: 11,
		letterSpacing: '.24em',
		textTransform: 'uppercase',
		color: mono.faint,
		fontWeight: 500,
	},
	'.shipHeadRow': { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' },
	'.shipNumber': {
		margin: 0,
		fontSize: 'clamp(28px, 3.2vw, 40px)',
		fontWeight: 200,
		lineHeight: 1.1,
		letterSpacing: '-0.01em',
		color: mono.ink,
		fontVariantNumeric: 'tabular-nums',
	},
	'.shipBox': { fontSize: 13, color: mono.dim, fontVariantNumeric: 'tabular-nums' },
	'.boChip': boShared['.boChip'],
	'.shipHeadMeta': { fontSize: 13, fontWeight: 300, color: mono.dim },
})

const style = css({
	...boShared,
	paddingTop: 8,

	'.statRow': {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
		gap: 24,
		padding: '8px 0 28px',
		borderBottom: `1px solid ${mono.line}`,
		'.boStatValue': { fontVariantNumeric: 'tabular-nums' },
		'.statText': { fontSize: 20, lineHeight: '28px', overflowWrap: 'anywhere' },
		'.statFaint': { color: mono.faint },
		a: { color: mono.ink },
	},

	'.boSection': {
		...boShared['.boSection'],
		'.boSectionTitle': { marginBottom: 8 },
	},

	'.boSpecRow': {
		...boShared['.boSpecRow'],
		gridTemplateColumns: 'minmax(0, 120px) minmax(0, 1fr)',
		'&.top': { alignItems: 'flex-start' },
		'&:last-of-type': { borderBottom: 0 },
	},
	'.boSpecValue': {
		...boShared['.boSpecValue'],
		minWidth: 0,
		overflowWrap: 'anywhere',
		'.block': { display: 'block' },
	},

	'.boNote': { fontSize: 12, color: mono.dim, marginTop: 8, lineHeight: 1.6 },

	'.formRow': {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: 12,
		marginBottom: 12,
		maxWidth: 960,
		'&.three': { gridTemplateColumns: 'repeat(3, 1fr)' },
	},

	'.lineTable': {
		width: '100%',
		borderCollapse: 'collapse',
		fontSize: 13,
		fontWeight: 300,
		color: mono.ink,
		'th, td': {
			padding: '8px 8px',
			textAlign: 'left',
			verticalAlign: 'middle',
			borderBottom: `1px solid ${mono.line}`,
		},
		th: {
			fontSize: 11,
			letterSpacing: '.12em',
			textTransform: 'uppercase',
			fontWeight: 500,
			color: mono.faint,
			whiteSpace: 'nowrap',
		},
		'.idx': { width: 32, color: mono.faint, fontVariantNumeric: 'tabular-nums' },
		'.num': {
			textAlign: 'right',
			fontVariantNumeric: 'tabular-nums',
			whiteSpace: 'nowrap',
		},
		'.itemCode': {
			display: 'inline-block',
			marginRight: 8,
			fontSize: 11,
			letterSpacing: '.08em',
			color: mono.faint,
		},
		'.serialInput': { height: 32, fontSize: 13 },
		'.boxSelect': {
			height: 32,
			border: 0,
			borderBottom: `1px solid ${mono.lineInput}`,
			background: 'transparent',
			fontFamily: boFont.ui,
			fontSize: 13,
			color: mono.ink,
		},
	},

	'.addRow': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 2fr) auto',
		gap: 12,
		alignItems: 'center',
		marginTop: 12,
		maxWidth: 720,
	},

	'.scanList': { marginTop: 8, maxHeight: 280, overflowY: 'auto' },
	'.scanRow': {
		display: 'grid',
		gridTemplateColumns: '92px 1fr 160px',
		gap: 12,
		padding: '8px 0',
		borderBottom: `1px solid ${mono.line}`,
		fontSize: 13,
		fontWeight: 300,
	},
	'.scanDate': { color: mono.dim, fontVariantNumeric: 'tabular-nums' },
	'.scanLoc': { color: mono.dim, textAlign: 'right' },

	'.actionBar': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
		paddingTop: 20,
		marginTop: 4,
		borderTop: `1px solid ${mono.line}`,
		'.actionLeft': { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
	},

	'.linkBtn': {
		fontSize: 13,
		color: mono.dim,
		textDecoration: 'underline',
		'&:hover': { color: mono.ink },
	},

	a: { color: mono.ink },

	// 태블릿·폰 (RESPONSIVE_PLAN §6.2)
	[boMedia.tablet]: {
		'.formRow.three': { gridTemplateColumns: 'repeat(2, 1fr)' },
	},
	[boMedia.phone]: {
		'.statRow': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
		'.formRow, .formRow.three': { gridTemplateColumns: '1fr', maxWidth: '100%' },
		'.addRow': { gridTemplateColumns: '1fr', maxWidth: '100%' },
		'.scanRow': { gridTemplateColumns: '1fr', gap: 2 },
		'.scanLoc': { textAlign: 'left' },
		// 2026-09-29: 가로 스크롤 표를 걷어냈다 — 폰에서는 한 행이 카드 한 장
		'.lineTable': boTableAsCards,
		'.actionBar': {
			position: 'sticky',
			bottom: 0,
			zIndex: 2,
			background: mono.bg,
			paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
			flexWrap: 'wrap',
			'& button': { minHeight: 44 },
		},
	},
})
